/**
 * Shared primitives for the Contracts portfolio drill-down.
 * Colours come exclusively from the STEEL & EMBER CSS variables so the same
 * markup is correct in both themes.
 */
import { motion } from 'framer-motion';

/** FastAPI returns a string detail for our own errors and an array for Pydantic
 *  validation errors — rendering the array crashes React, so flatten it. */
export const apiError = (e, fallback = 'Request failed') => {
  const d = e?.response?.data?.detail;
  if (!d) return e?.message || fallback;
  if (typeof d === 'string') return d;
  if (Array.isArray(d)) return d.map(x => x?.msg || JSON.stringify(x)).join('; ');
  return fallback;
};

export const fmtINR = (v) => {  if (v == null) return '—';
  const a = Math.abs(v);
  if (a >= 1e7) return `₹${(v / 1e7).toFixed(2)} Cr`;
  if (a >= 1e5) return `₹${(v / 1e5).toFixed(2)} L`;
  if (a >= 1e3) return `₹${(v / 1e3).toFixed(1)} K`;
  return `₹${Number(v).toLocaleString('en-IN', { maximumFractionDigits: 0 })}`;
};

export const fmtDate = (v) => {
  if (!v) return '—';
  try { return new Date(v).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: '2-digit' }); }
  catch { return '—'; }
};

export const riskHue = (s) => (s == null ? '#71717A' : s <= 30 ? '#15803D' : s <= 60 ? '#B45309' : '#B91C1C');

export const Panel = ({ title, hint, right, className = '', children, testId }) => (
  <section
    className={`surface-card rounded-xl ${className}`}
    data-testid={testId}
  >
    {(title || right) && (
      <header className="flex items-end justify-between gap-4 px-6 pt-5 pb-4 border-b"
              style={{ borderColor: 'var(--border-subtle)' }}>
        <div>
          <h3 className="text-xs font-semibold uppercase tracking-[0.18em]"
              style={{ color: 'var(--text-muted)' }}>{title}</h3>
          {hint && <p className="text-sm mt-1" style={{ color: 'var(--text-secondary)' }}>{hint}</p>}
        </div>
        {right}
      </header>
    )}
    <div className="px-6 py-5">{children}</div>
  </section>
);

export const Metric = ({ label, value, sub, accent, delta, deltaLabel, invert = false, index = 0, testId }) => {
  const good = delta == null ? null : invert ? delta < 0 : delta > 0;
  return (
  <motion.div
    initial={{ opacity: 0, y: 10 }}
    animate={{ opacity: 1, y: 0 }}
    transition={{ duration: 0.3, delay: index * 0.04, ease: 'easeOut' }}
    className="surface-card rounded-xl px-5 py-5 flex flex-col justify-between min-h-[118px] relative overflow-hidden"
    data-testid={testId}
  >
    <span className="absolute left-0 top-0 h-full w-[3px]"
          style={{ background: accent || 'var(--brand-accent)' }} />
    <p className="text-[10px] font-semibold uppercase tracking-[0.22em]"
       style={{ color: 'var(--text-muted)' }}>{label}</p>
    <p className="text-3xl font-light tabular-nums mt-3 leading-none"
       style={{ color: 'var(--text-primary)', fontFamily: '"JetBrains Mono", ui-monospace, monospace' }}>
      {value}
    </p>
    <div className="flex items-center justify-between gap-2 mt-2">
      <span className="text-[11px]" style={{ color: 'var(--text-secondary)' }}>{sub}</span>
      {delta != null && (
        <span className="text-[11px] font-semibold tabular-nums"
              style={{ color: delta === 0 ? 'var(--text-muted)' : good ? '#15803D' : '#B45309' }}>
          {delta > 0 ? '▲' : delta < 0 ? '▼' : '—'} {Math.abs(delta)}%
          {deltaLabel && <span className="font-normal ml-1" style={{ color: 'var(--text-muted)' }}>{deltaLabel}</span>}
        </span>
      )}
    </div>
  </motion.div>
  );
};

export const RiskPill = ({ score, testId }) => (
  <span
    className="inline-flex items-center gap-1 rounded-full px-2.5 py-0.5 text-[11px] font-semibold tabular-nums border"
    style={{ color: riskHue(score), borderColor: `${riskHue(score)}55`, background: `${riskHue(score)}14` }}
    data-testid={testId}
  >
    {score == null ? 'not reviewed' : `risk ${score}`}
  </span>
);

export const StatusPill = ({ status }) => (
  <span className="inline-flex rounded-full border px-2.5 py-0.5 text-[10px] font-semibold uppercase tracking-wider"
        style={{ borderColor: 'var(--border-color)', color: 'var(--text-secondary)' }}>
    {String(status || 'draft').replace(/_/g, ' ')}
  </span>
);

/** Horizontal share-of-total bar used in every level's concentration list. */
export const ShareBar = ({ pct, hue }) => (
  <span className="block h-1.5 w-full rounded-full overflow-hidden" style={{ background: 'var(--surface-bg)' }}>
    <span className="block h-full rounded-full" style={{ width: `${Math.max(2, pct)}%`, background: hue || 'var(--brand-accent)' }} />
  </span>
);

export const DrillRow = ({ onClick, children, testId }) => (
  <motion.button
    type="button"
    onClick={onClick}
    whileHover={{ y: -1 }}
    transition={{ duration: 0.18 }}
    className="w-full text-left px-5 py-4 grid gap-4 items-center border-b last:border-b-0 focus-visible:outline-none focus-visible:ring-2"
    style={{ borderColor: 'var(--border-subtle)' }}
    data-testid={testId}
  >
    {children}
  </motion.button>
);
